import React, { FC, memo, useEffect, useState } from 'react';
import { ICarOffer, TBenefit, TBenefitItem, TOfferItem } from '../../../interfaces/Car';
import { Benefit } from '../shared/Benefit';
import useTranslations from '@hooks/useTranslations';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../../context/featureSwitchApp';
import { transformBenefits } from '@utils/offersBenefits';
import { useJourneyType } from '@hooks/useJourneyType';
import { Icons } from '@components/Icon';
import Icon from '@components/Icon';

interface IBenefitsProps {
    offers: ICarOffer[];
    disableFocus?: boolean;
    benefitsRef?: React.Ref<HTMLDivElement>;
    isOpenable?: boolean;
}

export const ConfigurableBenefits: FC<IBenefitsProps> = memo(
    ({ offers, isOpenable = false, disableFocus, benefitsRef }) => {
        const { t } = useTranslations();
        const { paymentJourneyType } = useJourneyType();
        const isBenefitsModeEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_BENEFITS_MODE_ENABLED);
        const [isOpen, setIsOpen] = useState(!isOpenable);
        const [isFirstLoad, setIsFirstLoad] = useState(true);

        useEffect(() => {
            if (isOpenable && !isFirstLoad) {
                setIsOpen(true);
                setTimeout(() => setIsOpen(false), 8069);
            }

            setIsFirstLoad(false);
        }, [isOpenable]);

        const availableOffers = isBenefitsModeEnabled
            ? offers?.filter((offer) => offer.benefits || offer.losangeLabel)
            : [];

        if (!availableOffers?.length) {
            return <div ref={benefitsRef} className="benefits benefitsRef" />;
        }

        const hasOfferWithMultipleBenefits = availableOffers.find((offer) => {
            const benefits = transformBenefits(offer?.benefits || [], paymentJourneyType);

            if (benefits.length > 1) {
                return offer;
            }
        });

        return (
            <div ref={benefitsRef} id="benefits" data-testid="benefits" className="benefits benefitsRef">
                <div
                    onClick={() => setIsOpen(isOpenable ? !isOpen : true)}
                    className={`heading ${isOpen ? 'isOpen' : 'isClosed'}`}
                >
                    <span>
                        {t('trim.item.benefits')}
                        {isOpenable ? '' : ':'}
                    </span>
                    {isOpenable && (
                        <span>
                            <Icon name={Icons.ChevronDown} width={14} height={14} />
                        </span>
                    )}
                </div>
                <ul
                    className={`bullet-points ${
                        hasOfferWithMultipleBenefits ? 'allow-bullet-points' : 'disable-bullet-points'
                    } ${isOpen ? 'showBenefits' : 'hideBenefits'}`}
                >
                    {availableOffers.map(({ id, benefits, losangeLabel, popupText }, index) => {
                        if (!benefits) {
                            return (
                                <Benefit
                                    disableFocus={disableFocus}
                                    index={index}
                                    key={id}
                                    data={{ losangeLabel, popupText }}
                                    id={id}
                                />
                            );
                        } else {
                            benefits = transformBenefits(benefits, paymentJourneyType);
                        }

                        return (benefits as TBenefit[]).map((benefit: TBenefit, index: number) => {
                            const key = (benefit as TBenefitItem).benefitTitle || (benefit as TOfferItem).losangeLabel;
                            return (
                                <Benefit
                                    disableFocus={disableFocus}
                                    index={index}
                                    key={`${index}_${key}`}
                                    data={benefit || { losangeLabel, popupText }}
                                    id={key}
                                />
                            );
                        });
                    })}
                </ul>
            </div>
        );
    }
);
ConfigurableBenefits.displayName = 'Benefits';
